import { AgeDivisionPayload } from "@bracket/idl/main/fight/model/enums/AgeDivisionPt"
import {
  FightSportPayload,
  fightSportPayloadFromJSON,
} from "@bracket/idl/main/fight/model/enums/FightSportPt"
import { GenderPayload } from "@bracket/idl/main/fight/model/enums/GenderPt"
import type { WeightClassPayload } from "@bracket/idl/main/fight/model/FightPt"

/**
 * 종목 · 부 · 성별 · 체급 표시 이름.
 *
 * **여기가 라벨의 단일 출처다.** 화면마다 따로 두면 같은 종목이 "복싱"과 "아마추어복싱"으로 갈린다.
 */
const SPORT_LABELS: Record<FightSportPayload, string> = {
  [FightSportPayload.FIGHT_SPORT_UNKNOWN]: "미지정",
  [FightSportPayload.AMATEUR_BOXING]: "아마추어복싱",
  [FightSportPayload.IMMAF_MMA]: "IMMAF 종합격투기",
  [FightSportPayload.MUAY_THAI_KICKBOXING]: "무에타이 · 킥복싱",
  [FightSportPayload.PRO_MMA]: "프로 MMA",
  [FightSportPayload.CHICKEN_FIGHT]: "닭싸움",
  [FightSportPayload.MUAY_THAI_KICKBOXING_NO_FACE]:
    "무에타이 · 킥복싱(안면타격 X)",
  [FightSportPayload.NO_GI_JIU_JITSU]: "노기 주짓수",
  [FightSportPayload.UNRECOGNIZED]: "미지정",
}

const GENDER_LABELS: Record<GenderPayload, string> = {
  [GenderPayload.GENDER_UNKNOWN]: "미지정",
  [GenderPayload.GENDER_MALE]: "남",
  [GenderPayload.GENDER_FEMALE]: "여",
  [GenderPayload.GENDER_ANY]: "공통",
  [GenderPayload.UNRECOGNIZED]: "미지정",
}

const AGE_DIVISION_LABELS: Record<AgeDivisionPayload, string> = {
  [AgeDivisionPayload.AGE_DIVISION_UNKNOWN]: "미지정",
  [AgeDivisionPayload.AGE_DIVISION_ELEMENTARY_LOWER]: "초등부 저학년",
  [AgeDivisionPayload.AGE_DIVISION_ELEMENTARY_UPPER]: "초등부 고학년",
  [AgeDivisionPayload.AGE_DIVISION_ELEMENTARY]: "초등부",
  [AgeDivisionPayload.AGE_DIVISION_MIDDLE_SCHOOL]: "중등부",
  [AgeDivisionPayload.AGE_DIVISION_HIGH_SCHOOL]: "고등부",
  [AgeDivisionPayload.AGE_DIVISION_ADULT]: "성인부",
  [AgeDivisionPayload.UNRECOGNIZED]: "미지정",
}

/** 신청 폼의 종목 선택지 — 대회가 개최하는 종목만 남긴다 */
const SPORT_OPTIONS = [
  {
    value: "AMATEUR_BOXING",
    label: SPORT_LABELS[FightSportPayload.AMATEUR_BOXING],
  },
  { value: "IMMAF_MMA", label: SPORT_LABELS[FightSportPayload.IMMAF_MMA] },
  {
    value: "MUAY_THAI_KICKBOXING",
    label: SPORT_LABELS[FightSportPayload.MUAY_THAI_KICKBOXING],
  },
  { value: "PRO_MMA", label: SPORT_LABELS[FightSportPayload.PRO_MMA] },
  {
    value: "CHICKEN_FIGHT",
    label: SPORT_LABELS[FightSportPayload.CHICKEN_FIGHT],
  },
  {
    value: "MUAY_THAI_KICKBOXING_NO_FACE",
    label: SPORT_LABELS[FightSportPayload.MUAY_THAI_KICKBOXING_NO_FACE],
  },
  {
    value: "NO_GI_JIU_JITSU",
    label: SPORT_LABELS[FightSportPayload.NO_GI_JIU_JITSU],
  },
]

function sportLabel(sport: FightSportPayload): string {
  return SPORT_LABELS[sport] ?? "미지정"
}

/**
 * 그 종목이 선수 기록(체중 · 경력 · 출전 횟수)과 체급을 받는지.
 *
 * **닭싸움만 받지 않는다** — 무체급 하나뿐이라 고를 체급이 없고(서버가 넣는다),
 * 체중으로 나누지 않으니 기록을 물을 이유도 없다. 신청서는 이름 · 생년월일 · 성별 · 종목
 * 넷만 받는다. 서버도 같은 규칙이다(`FightSportCodes.isPlayerRecordCollected`).
 */
function isPlayerRecordCollected(sport: FightSportPayload): boolean {
  return sport !== FightSportPayload.CHICKEN_FIGHT
}

/** 신청 폼이 다루는 종목 값(IDL enum 이름)으로 묻는다 — 아직 고르지 않았으면 「받는다」다 */
function isPlayerRecordCollectedByValue(sportValue: string): boolean {
  return isPlayerRecordCollected(fightSportPayloadFromJSON(sportValue))
}

function genderLabel(gender: GenderPayload): string {
  return GENDER_LABELS[gender] ?? "미지정"
}

function divisionLabel(division: AgeDivisionPayload): string {
  return AGE_DIVISION_LABELS[division] ?? "미지정"
}

/** 신청 폼의 체급 선택지 — 서버가 내려준 payload 를 그대로 들고 있다 (다시 만들지 않고 요청에 실어 보낸다) */
interface WeightClassOption {
  /** 표시명 = 선택 키 (종목 · 부 · 성별 안에서 유일하다) */
  key: string
  label: string
  payload: WeightClassPayload
}

/** 선수 한 명의 체급 선택지 상태 */
interface WeightClassChoicesState {
  options: WeightClassOption[]
  /** 고를 수 없는 이유 (선행 입력 필요 · 체급표 없음 · 조회 실패) */
  notice: string
  isLoading: boolean
  /**
   * 서버에 물어본 결과인지 — 선행 입력이 덜 찼거나 조회가 실패하면 false 다.
   * 물어본 결과 선택지가 0개면 그 부의 체급표가 없어 운영자가 나중에 확정한다.
   */
  isAsked: boolean
}

/** 부를 알아야 체급이 정해지므로 생년월일 · 성별 · 종목이 다 있어야 물을 수 있다 (입력 칸도 그 순서로 놓는다) */
const WEIGHT_CLASS_PREREQUISITE_NOTICE =
  "생년월일 · 성별 · 종목을 먼저 입력해 주세요."

/**
 * 체급 표시명.
 *
 * 서버가 `name` 을 채워 내려주므로 화면은 그 값을 쓰고, 미분류면 안내 문구로 바꾼다.
 */
function weightClassLabel(weightClass?: WeightClassPayload): string {
  if (!weightClass || !weightClass.name) return "미분류"

  return weightClass.name
}

export {
  AGE_DIVISION_LABELS,
  GENDER_LABELS,
  SPORT_LABELS,
  SPORT_OPTIONS,
  WEIGHT_CLASS_PREREQUISITE_NOTICE,
  divisionLabel,
  genderLabel,
  isPlayerRecordCollected,
  isPlayerRecordCollectedByValue,
  sportLabel,
  weightClassLabel,
}
export type { WeightClassChoicesState, WeightClassOption }
